import React, { useMemo } from "react";
import { DashTotalRevenueCardSkeleton } from "./DashCardSkeletons";
import { AddCommas, primaryColor } from "../../../../config";
import { getAmount } from "../../../../utils/helper";

function formatTrendPct(pct) {
  if (pct == null || Number.isNaN(pct)) return "—";
  const abs = Math.abs(pct);
  const arrow = pct > 0 ? "↑" : pct < 0 ? "↓" : "→";
  return `${arrow}${abs}%`;
}

/** Thin white columns, rounded tops (reference Total Revenue chart). */
function RevenueBarsOnGreen({ values, height = 200 }) {
  const vals = (values || []).map((v) => Number(v) || 0);
  if (!vals.length) return null;
  const max = Math.max(...vals, 1);
  const vbW = 320;
  const vbH = height;
  const padX = 10;
  const padB = 10;
  const padT = 6;
  const plotH = vbH - padB - padT;
  const slot = (vbW - padX * 2) / vals.length;

  return (
    <svg
      className="dash-total-revenue-card__svg mb-n6"
      viewBox={`0 0 ${vbW} ${vbH}`}
      width="100%"
      height={vbH}
      preserveAspectRatio="none"
      aria-hidden
    >
      {vals.map((v, i) => {
        const barH = (v / max) * plotH;
        const cx = padX + i * slot + slot / 2;
        const barW = Math.max(Math.min(slot * 0.22, 5), 1.8);
        const yBase = vbH - padB;
        const yTop = yBase - barH;
        return (
          <rect
            key={i}
            x={cx - barW / 2}
            y={yTop}
            width={barW}
            height={barH}
            rx={2}
            fill={primaryColor}
          />
        );
      })}
    </svg>
  );
}

/**
 * Total Revenue — green chart + main revenue & trend + Order | AOV | units (reference layout).
 */
export default function DashTotalRevenueCard({
  loading,
  totals,
  series,
  trend,
  ordersTotal,
  unitsTotal,
  currencySign = "₹",
}) {
  const salesSeries = series?.sales || [];
  const ordersSeries = series?.orders || [];
  const unitsSeries = series?.units || [];

  const orderSum = useMemo(
    () => ordersTotal ?? ordersSeries.reduce((a, b) => a + (Number(b) || 0), 0),
    [ordersTotal, ordersSeries]
  );
  const unitSum = useMemo(
    () => unitsTotal ?? unitsSeries.reduce((a, b) => a + (Number(b) || 0), 0),
    [unitsTotal, unitsSeries]
  );
  const aov = orderSum > 0 ? (totals?.sales || 0) / orderSum : 0;

  const pct = trend?.pct;
  const trendUp = pct != null && pct > 0;
  const trendDown = pct != null && pct < 0;
  const trendFlat = pct != null && pct === 0;
  let trendClass = "dash-total-revenue-card__trend--muted";
  if (!trendFlat && pct != null) {
    if (trendUp) trendClass = "dash-total-revenue-card__trend--up";
    else if (trendDown) trendClass = "dash-total-revenue-card__trend--down";
  }

  const showChart = salesSeries.length > 0;
  const mainRevenue = getAmount(totals?.sales || 0, currencySign, { decimals: 2 });

  if (loading) {
    return (
      <div className="dash-total-revenue-card h-100">
        <DashTotalRevenueCardSkeleton />
      </div>
    );
  }

  return (
    <div className="dash-total-revenue-card h-100">
      <div className="dash-total-revenue-card__chart-panel">
        <div className="dash-total-revenue-card__chart-head">
          <span className="dash-total-revenue-card__title">Total Revenue</span>
            {/* <AppstoreOutlined className="dash-total-revenue-card__icon" /> */}
        </div>
        <div className="dash-total-revenue-card__chart-body">
          {showChart ? (
            <RevenueBarsOnGreen values={salesSeries} height={80} />
          ) : (
            <div className="dash-total-revenue-card__empty small py-10 text-center">
              No revenue data for this range
            </div>
          )}
        </div>
      </div>

      <div className="dash-total-revenue-card__body">
          <>
            <div className="dash-total-revenue-card__primary mt-auto">
              <div className="dash-total-revenue-card__primary-value fs-2">{mainRevenue}</div>
              {pct != null ? (
                <div className="dash-total-revenue-card__trend fs-8">
                  {trend?.delta != null ? (
                    <span className="dash-total-revenue-card__trend-delta">
                      {getAmount(trend.delta, currencySign)}
                    </span>
                  ) : null}
                  <span className="dash-total-revenue-card__trend-pct">
                    <span className={` ${trendClass}`}>{formatTrendPct(pct)}</span> 
                    <span className="text-muted fw-normal fs-8 ms-1">vs period</span>
                    </span>
                </div>
              ) : null}
            </div>

            <div className="dash-total-revenue-card__subrow">
              <div className="dash-total-revenue-card__subcol">
                <div className="dash-total-revenue-card__subvalue">
                  {AddCommas(Math.round(orderSum))}
                </div>
                <div className="dash-total-revenue-card__sublabel">Orders</div>
              </div>
              <div className="dash-total-revenue-card__subcol dash-total-revenue-card__subcol--solid">
                <div className="dash-total-revenue-card__subvalue">
                  {getAmount(aov, currencySign, { decimals: 0 })}
                </div>
                <div className="dash-total-revenue-card__sublabel">AOV</div>
              </div>
              <div className="dash-total-revenue-card__subcol dash-total-revenue-card__subcol--solid">
                <div className="dash-total-revenue-card__subvalue">
                  {AddCommas(Math.round(unitSum))}
                </div>
                <div className="dash-total-revenue-card__sublabel">Units</div>
              </div>
            </div>
          </>
      </div>
    </div>
  );
}
